import { api } from "./client";
import type { ApiResponse } from "./types";

// ============================================================================
// Provider messages — a member asking a business something.
//
// This is the only place on WeDeshi where a member talks to a BUSINESS rather
// than to another person, which is why it is a thread rather than a wave: the
// business is answering in a work capacity and needs a record of it.
//
// It is also the lead pipeline. The shop desk's "someone is asking" block is
// built from exactly these threads, unread-first, and that ordering is a
// contract rather than a preference — an owner only learns where leads live if
// they are always in the same place.
// ============================================================================

export type ProviderThreadStatus = "open" | "closed";

export interface ProviderThreadSummary {
  threadId: string;
  status: ProviderThreadStatus;
  isUnread: boolean;
  lastMessageAt: string;
  provider: { id: string; name: string; slug: string };
  from: { id: string; name: string; city: string | null };
  preview: string;
}

export interface ProviderMessage {
  id: string;
  body: string;
  createdAt: string;
  sender: { id: string; name: string };
  /** True when the business wrote it rather than the member. */
  isFromProvider: boolean;
}

export interface ProviderThread {
  threadId: string;
  status: ProviderThreadStatus;
  provider: { id: string; name: string; slug: string };
  messages: ProviderMessage[];
}

/** Both sides read this — a member sees their enquiries, an owner sees leads. */
export function listProviderThreads(params?: { status?: ProviderThreadStatus }) {
  return api.get<ApiResponse<{ threads: ProviderThreadSummary[] }>>(
    "/provider-messages",
    params as Record<string, string | number | boolean | undefined>,
  );
}

/** Drives the badge. Cheap enough to poll; it is a count, not a list. */
export function getProviderUnreadCount() {
  return api.get<ApiResponse<{ unread: number }>>("/provider-messages/unread-count");
}

export function getProviderThread(threadId: string) {
  return api.get<ApiResponse<ProviderThread>>(`/provider-messages/${threadId}`);
}

/** One `idempotencyKey` per reply; a retry with the same key returns the same reply. */
export function replyToProviderThread(threadId: string, body: string, idempotencyKey?: string) {
  return api.post<ApiResponse<{ message: ProviderMessage }>>(
    `/provider-messages/${threadId}/reply`,
    { body, ...(idempotencyKey ? { idempotencyKey } : {}) },
  );
}

/**
 * Mark read. Separate from fetching on purpose — opening a thread to skim it
 * and deciding you have dealt with it are different acts, and a read receipt
 * that fires on render makes the unread count useless as a worklist.
 */
export function markProviderThreadRead(threadId: string) {
  return api.post<ApiResponse<{ message: string }>>(`/provider-messages/${threadId}/read`, {});
}

export function closeProviderThread(threadId: string) {
  return api.post<ApiResponse<{ message: string }>>(`/provider-messages/${threadId}/close`, {});
}

/** Moderation path for a single message, not the whole thread. */
export function reportProviderMessage(messageId: string, reason: string) {
  return api.post<ApiResponse<{ message: string }>>(
    `/provider-messages/messages/${messageId}/report`,
    { reason },
  );
}
